import { useCallback, useEffect, useRef, useState } from 'react';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import type { DropboxFolderOption } from '../dropbox.types';
import { buildDropboxPath } from '../dropbox.utils';
import type { DropboxFolderTreePickerProps } from './DropboxFolderTreePicker.props';

const isMetaTreeItem = (itemId: string) =>
  itemId.endsWith('__loading') || itemId.endsWith('__empty') || itemId.endsWith('__placeholder');

const DropboxFolderTreePicker = ({
  rootPath,
  rootLabel,
  selectedPath,
  onSelect,
  onLoadChildren,
  disabled = false,
  suggestedFolderName,
  maxHeight = 280,
}: DropboxFolderTreePickerProps) => {
  const normalizedRootPath = buildDropboxPath(rootPath);
  const [childrenByPath, setChildrenByPath] = useState<Record<string, DropboxFolderOption[]>>({});
  const [loadingPaths, setLoadingPaths] = useState<Set<string>>(new Set());
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const loadedPathsRef = useRef<Set<string>>(new Set());
  const loadingPathsRef = useRef<Set<string>>(new Set());

  const loadChildren = useCallback(
    async (path: string) => {
      const normalizedPath = buildDropboxPath(path);
      if (loadedPathsRef.current.has(normalizedPath) || loadingPathsRef.current.has(normalizedPath)) {
        return;
      }

      loadingPathsRef.current.add(normalizedPath);
      setLoadingPaths(current => new Set(current).add(normalizedPath));
      setLoadError(null);
      try {
        const children = await onLoadChildren(normalizedPath);
        loadedPathsRef.current.add(normalizedPath);
        setChildrenByPath(current => ({ ...current, [normalizedPath]: children }));
      } catch {
        setLoadError('Unable to load folders. Check your Dropbox connection and try again.');
      } finally {
        loadingPathsRef.current.delete(normalizedPath);
        setLoadingPaths(current => {
          const next = new Set(current);
          next.delete(normalizedPath);
          return next;
        });
      }
    },
    [onLoadChildren]
  );

  useEffect(() => {
    if (!normalizedRootPath || disabled) {
      return;
    }
    loadedPathsRef.current = new Set();
    loadingPathsRef.current = new Set();
    setChildrenByPath({});
    setLoadingPaths(new Set());
    setLoadError(null);
    setExpandedItems([normalizedRootPath]);
    loadChildren(normalizedRootPath);
  }, [disabled, loadChildren, normalizedRootPath]);

  const handleExpandedItemsChange = (_event: React.SyntheticEvent | null, itemIds: string[]) => {
    const newlyExpanded = itemIds.filter(itemId => !expandedItems.includes(itemId));
    setExpandedItems(itemIds);
    newlyExpanded.forEach(itemId => {
      if (!isMetaTreeItem(itemId)) {
        loadChildren(itemId);
      }
    });
  };

  const handleSelectedItemsChange = (_event: React.SyntheticEvent | null, itemId: string | null) => {
    if (!itemId || itemId === normalizedRootPath || disabled || isMetaTreeItem(itemId)) {
      return;
    }

    const selectedFolder = Object.values(childrenByPath)
      .flat()
      .find(folder => folder.pathDisplay === itemId);

    if (selectedFolder) {
      onSelect(selectedFolder);
    }
  };

  const renderFolderLabel = (name: string, isSuggested = false) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <FolderOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      <Typography
        variant="body2"
        sx={{ fontWeight: isSuggested ? 600 : 400, color: isSuggested ? 'primary.main' : 'text.primary' }}
      >
        {name}
      </Typography>
    </Box>
  );

  const renderLoadingItem = (path: string) => (
    <TreeItem
      itemId={`${path}__loading`}
      label={(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            Loading folders...
          </Typography>
        </Box>
      )}
    />
  );

  const renderEmptyItem = (path: string) => (
    <TreeItem
      itemId={`${path}__empty`}
      label={(
        <Typography variant="caption" color="text.secondary">
          No subfolders
        </Typography>
      )}
    />
  );

  // Unloaded folders need a placeholder child so MUI shows an expand icon (otherwise they look like leaves).
  const renderPlaceholderItem = (path: string) => (
    <TreeItem
      itemId={`${path}__placeholder`}
      label={(
        <Typography variant="caption" color="text.secondary">
          …
        </Typography>
      )}
    />
  );

  const renderFolderNodes = (parentPath: string) => {
    const children = childrenByPath[parentPath] ?? [];

    return children.map(folder => {
      const isSuggested = Boolean(
        suggestedFolderName?.toLowerCase() === folder.name.toLowerCase()
      );
      const isLoadingChildren = loadingPaths.has(folder.pathDisplay);
      const hasLoadedChildren = Object.prototype.hasOwnProperty.call(childrenByPath, folder.pathDisplay);
      const loadedChildren = childrenByPath[folder.pathDisplay] ?? [];

      return (
        <TreeItem
          key={folder.pathDisplay}
          itemId={folder.pathDisplay}
          label={renderFolderLabel(folder.name, isSuggested)}
        >
          {isLoadingChildren && renderLoadingItem(folder.pathDisplay)}
          {!isLoadingChildren && !hasLoadedChildren && renderPlaceholderItem(folder.pathDisplay)}
          {!isLoadingChildren && hasLoadedChildren && loadedChildren.length === 0 && renderEmptyItem(folder.pathDisplay)}
          {!isLoadingChildren && hasLoadedChildren && renderFolderNodes(folder.pathDisplay)}
        </TreeItem>
      );
    });
  };

  const isRootLoading = loadingPaths.has(normalizedRootPath);
  const rootChildren = childrenByPath[normalizedRootPath] ?? [];

  return (
    <Box sx={{ width: '100%' }}>
      {loadError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {loadError}
        </Alert>
      )}
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          // Fixed height (not only maxHeight) so expanding nodes cannot grow the popover.
          height: maxHeight,
          maxHeight,
          overflow: 'auto',
          overscrollBehavior: 'auto',
          p: 1,
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        <SimpleTreeView
          expandedItems={expandedItems}
          onExpandedItemsChange={handleExpandedItemsChange}
          selectedItems={selectedPath}
          onSelectedItemsChange={handleSelectedItemsChange}
        >
          <TreeItem
            itemId={normalizedRootPath}
            label={renderFolderLabel(rootLabel)}
            sx={{ '& .MuiTreeItem-label:hover': { backgroundColor: 'transparent !important' } }}
          >
            {isRootLoading && renderLoadingItem(normalizedRootPath)}
            {!isRootLoading && rootChildren.length === 0 && renderEmptyItem(normalizedRootPath)}
            {!isRootLoading && renderFolderNodes(normalizedRootPath)}
          </TreeItem>
        </SimpleTreeView>
      </Box>
    </Box>
  );
};

export default DropboxFolderTreePicker;
